import { useQuery } from '@tanstack/react-query';
import { storiesApi, apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/features/auth/store/authStore';
import type { StoryLibraryItem } from '@/features/library/components/StoryCard';

interface ActivityRecord {
  resource_id: string;
  logged_at: string;
}

interface EnrichedStory extends StoryLibraryItem {
  has_completed: boolean;
}

export function useLibrary(viewMode: 'all' | 'history' = 'all') {
  const user = useAuthStore((state) => state.user);
  const isParent = user?.roles?.includes('parent');
  const isStudent = user?.roles?.includes('student');

  return useQuery({
    queryKey: ['library', isParent ? 'all' : (viewMode === 'history' ? 'history' : 'all')],
    queryFn: async () => {
      if (isParent) {
        return storiesApi.getAllStories();
      }

      if (isStudent) {
        // Fetch all stories first
        const allStories = await storiesApi.getAllStories();

        // Get story_read_end activities to see which stories were completed
        const completedActivities = await apiClient.get<ActivityRecord[]>('/activities/', {
          params: {
            activity_type: 'story_read_end',
            resource_type: 'story',
            limit: 500
          }
        });

        // Create a map of story_id -> last completed time
        const completedMap = new Map<string, { lastCompleted: Date; readCount: number }>();
        completedActivities.forEach((activity: ActivityRecord) => {
          const storyId = activity.resource_id;
          const completedAt = new Date(activity.logged_at);

          if (!completedMap.has(storyId)) {
            completedMap.set(storyId, { lastCompleted: completedAt, readCount: 1 });
          } else {
            const existing = completedMap.get(storyId)!;
            existing.readCount++;
            if (completedAt > existing.lastCompleted) {
              existing.lastCompleted = completedAt;
            }
          }
        });

        // Enrich stories with completion metadata
        const enrichedStories: EnrichedStory[] = allStories.map((story: StoryLibraryItem) => {
          const completed = completedMap.get(story.id);
          return {
            ...story,
            last_accessed: completed?.lastCompleted.toISOString(),
            attempt_count: completed?.readCount || 0,
            has_completed: !!completed
          };
        });

        if (viewMode === 'history') {
          // Filter to only completed stories and sort by most recent
          return enrichedStories
            .filter((story: EnrichedStory) => story.has_completed)
            .sort((a: EnrichedStory, b: EnrichedStory) => {
              const aTime = a.last_accessed ? new Date(a.last_accessed).getTime() : 0;
              const bTime = b.last_accessed ? new Date(b.last_accessed).getTime() : 0;
              return bTime - aTime;
            });
        }

        // Return all stories with completion status
        return enrichedStories;
      }

      return storiesApi.getAllStories(); // Default: show all stories
    },
  });
}
